import React, { useState } from 'react';
import { Mail, Phone, Instagram, Send, MapPin, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';
import { submitContactForm } from '../utils/api';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language].contact;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await submitContactForm(formData);
      toast.success(
        language === 'en'
          ? 'Message sent successfully! We\'ll get back to you soon.'
          : language === 'es'
          ? '¡Mensaje enviado con éxito! Te responderemos pronto.'
          : 'Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.'
      );
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(
        language === 'en'
          ? 'Failed to send message. Please try again.'
          : language === 'es'
          ? 'Error al enviar el mensaje. Por favor inténtalo de nuevo.'
          : 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#141619] to-[#0a0c0f] circuit-pattern relative">
      {/* Decorative line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"></div>
      
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 mb-6 px-4 py-2 bg-gradient-to-r from-[#1a1d23] to-[#141619] rounded-full border border-[#D4AF37]/30">
            <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37] font-semibold">
              {language === 'en' ? 'Contact Us' : language === 'es' ? 'Contáctanos' : 'Kontakt'}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="text-gold-metallic">{t.title.split(' ')[0]} {t.title.split(' ')[1]}</span> {t.title.split(' ').slice(2).join(' ')}
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] p-8 rounded-2xl border border-[#2a3441] relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                <Send className="w-6 h-6 text-[#D4AF37]" />
                <span>{language === 'en' ? 'Send us a message' : language === 'es' ? 'Envíanos un mensaje' : 'Senden Sie uns eine Nachricht'}</span>
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {language === 'en' ? 'Name' : language === 'es' ? 'Nombre' : 'Name'}
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t.namePlaceholder}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-[#141619] border-[#2a3441] text-white focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t.emailPlaceholder}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-[#141619] border-[#2a3441] text-white focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {language === 'en' ? 'Message' : language === 'es' ? 'Mensaje' : 'Nachricht'}
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t.messagePlaceholder}
                    required
                    rows={6}
                    className="w-full px-4 py-3 rounded-lg bg-[#141619] border-[#2a3441] text-white focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-300 resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] rounded-full font-bold text-sm uppercase tracking-widest hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed btn-premium"
                >
                  <span>{submitting ? (language === 'en' ? 'Sending...' : language === 'es' ? 'Enviando...' : 'Senden...') : t.submit}</span>
                  <Send className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] p-8 rounded-2xl border border-[#2a3441] relative overflow-hidden">
              {/* Decorative element */}
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#5A6F8F]/5 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-6">
                  {t.or}
                </h3>

                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start space-x-4 group">
                    <div className="p-3 bg-gradient-to-br from-[#D4AF37] to-[#F4C542] rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Phone className="w-6 h-6 text-[#141619]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">
                        {language === 'en' ? 'Phone' : language === 'es' ? 'Teléfono' : 'Telefon'}
                      </div>
                      <a
                        href="tel:+34633368186"
                        className="text-lg font-medium text-white hover:text-[#D4AF37] transition-colors duration-300"
                      >
                        +34-633368186
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start space-x-4 group">
                    <div className="p-3 bg-gradient-to-br from-[#D4AF37] to-[#F4C542] rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Mail className="w-6 h-6 text-[#141619]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">
                        Email
                      </div>
                      <a
                        href="mailto:Info@primedigital.es"
                        className="text-lg font-medium text-white hover:text-[#D4AF37] transition-colors duration-300 break-all"
                      >
                        Info@primedigital.es
                      </a>
                    </div>
                  </div>

                  {/* Instagram */}
                  <div className="flex items-start space-x-4 group">
                    <div className="p-3 bg-gradient-to-br from-[#5A6F8F] to-[#6A7F9F] rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Instagram className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">
                        Instagram
                      </div>
                      <a
                        href="https://instagram.com/primedigital.es"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lg font-medium text-white hover:text-[#D4AF37] transition-colors duration-300"
                      >
                        @primedigital.es
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gradient-to-br from-[#1a1d23] to-[#141619] p-6 rounded-2xl border border-[#2a3441]">
              <h4 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
                <span>{language === 'en' ? 'Business Hours' : language === 'es' ? 'Horario de Atención' : 'Geschäftszeiten'}</span>
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-[#2a3441]">
                  <span className="text-gray-400">{language === 'en' ? 'Monday - Friday' : language === 'es' ? 'Lunes - Viernes' : 'Montag - Freitag'}</span>
                  <span className="font-medium text-[#D4AF37]">9:00 - 18:00</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#2a3441]">
                  <span className="text-gray-400">{language === 'en' ? 'Saturday' : language === 'es' ? 'Sábado' : 'Samstag'}</span>
                  <span className="font-medium text-gray-400">{language === 'en' ? 'Closed' : language === 'es' ? 'Cerrado' : 'Geschlossen'}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-400">{language === 'en' ? 'Sunday' : language === 'es' ? 'Domingo' : 'Sonntag'}</span>
                  <span className="font-medium text-gray-400">{language === 'en' ? 'Closed' : language === 'es' ? 'Cerrado' : 'Geschlossen'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#5A6F8F]/30 to-transparent"></div>
    </section>
  );
};

export default Contact;
